import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

type Scrape = {
  id: string;
  asin: string;
  productTitle: string | null;
  brandName: string | null;
  customerId: string | null;
  source: "auto" | "human";
  status: "pending" | "running" | "submitted" | "succeeded" | "failed" | "rejected";
  coverage: string | null;
  errorCategory: string | null;
  errorMessage: string | null;
  manualScraper: { id: string; email: string; name: string | null } | null;
  claimedAt: string | null;
  submittedAt: string | null;
  qaedAt: string | null;
  completedAt: string | null;
  rejectionReason: string | null;
};

const STATUSES = [
  "pending",
  "submitted",
  "succeeded",
  "failed",
  "rejected",
] as const;

const SOURCES = ["human", "auto"] as const;

function AdminScrapesInner() {
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") as Scrape["status"] | null) ?? null;
  const source = (params.get("source") as Scrape["source"] | null) ?? null;
  const [scrapes, setScrapes] = useState<Scrape[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    customerEmail: "",
    asin: "",
    brandName: "",
    productTitle: "",
    notes: "",
  });

  async function refresh() {
    setLoading(true);
    const q = new URLSearchParams();
    if (status) q.set("status", status);
    if (source) q.set("source", source);
    const res = await api<{ scrapes: Scrape[] }>(`/admin/scrapes?${q}`);
    if (res.ok && res.data) setScrapes(res.data.scrapes);
    setLoading(false);
  }

  useEffect(() => {
    void refresh();
  }, [status, source]);

  function updateFilter(next: { status?: Scrape["status"] | null; source?: Scrape["source"] | null }) {
    const q = new URLSearchParams(params);
    if ("status" in next) {
      if (next.status) q.set("status", next.status);
      else q.delete("status");
    }
    if ("source" in next) {
      if (next.source) q.set("source", next.source);
      else q.delete("source");
    }
    setParams(q);
  }

  async function createAssignment(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setCreating(true);
    const res = await api("/admin/scrapes", {
      method: "POST",
      body: JSON.stringify({
        customerEmail: form.customerEmail.trim(),
        asin: form.asin.trim(),
        brandName: form.brandName.trim() || undefined,
        productTitle: form.productTitle.trim() || undefined,
        notes: form.notes.trim() || undefined,
      }),
    });
    setCreating(false);
    if (!res.ok) {
      setError(res.error ?? "Could not create assignment.");
      return;
    }
    setForm({
      customerEmail: "",
      asin: "",
      brandName: "",
      productTitle: "",
      notes: "",
    });
    if (status && status !== "pending") setParams({ source: "human", status: "pending" });
    else void refresh();
  }

  return (
    <AdminLayout>
      <h1 className="text-xl font-semibold tracking-tight">Scrape queue</h1>
      <p className="mt-1 text-sm text-slate-600">
        Auto scrape attempts and manual review assignments. Open a failed row
        to see the full stored error.
      </p>

      <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 text-slate-900">
        <h2 className="text-sm font-semibold text-slate-900">
          Create assignment
        </h2>
        <form
          onSubmit={createAssignment}
          className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2"
        >
          <input
            required
            type="email"
            placeholder="Customer email"
            value={form.customerEmail}
            onChange={(e) =>
              setForm({ ...form, customerEmail: e.target.value })
            }
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400"
          />
          <input
            required
            type="text"
            placeholder="ASIN (e.g. B0FG7Y6R1L)"
            value={form.asin}
            onChange={(e) => setForm({ ...form, asin: e.target.value })}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-mono text-slate-900 placeholder:text-slate-400"
          />
          <input
            type="text"
            placeholder="Brand name (optional, defaults to 'Manual')"
            value={form.brandName}
            onChange={(e) => setForm({ ...form, brandName: e.target.value })}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400"
          />
          <input
            type="text"
            placeholder="Product title (optional, helps the scraper)"
            value={form.productTitle}
            onChange={(e) => setForm({ ...form, productTitle: e.target.value })}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400"
          />
          <textarea
            placeholder="Notes for the scraper (optional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 md:col-span-2"
            rows={2}
          />
          <div className="md:col-span-2">
            <Button type="submit" disabled={creating} size="sm">
              {creating ? "Creating…" : "Queue assignment"}
            </Button>
            {error && (
              <span className="ml-3 text-xs text-red-600" role="alert">
                {error}
              </span>
            )}
          </div>
        </form>
      </section>

      <div className="mt-6 flex flex-wrap items-center gap-2 text-sm">
        <button
          type="button"
          onClick={() => updateFilter({ status: null })}
          className={
            !status
              ? "rounded-full bg-slate-900 px-3 py-1 text-xs font-medium text-white"
              : "rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700 hover:bg-slate-200"
          }
        >
          All
        </button>
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => updateFilter({ status: s })}
            className={
              status === s
                ? "rounded-full bg-slate-900 px-3 py-1 text-xs font-medium text-white"
                : "rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700 hover:bg-slate-200"
            }
          >
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
        <button
          type="button"
          onClick={() => updateFilter({ source: null })}
          className={
            !source
              ? "rounded-full bg-slate-900 px-3 py-1 text-xs font-medium text-white"
              : "rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700 hover:bg-slate-200"
          }
        >
          All sources
        </button>
        {SOURCES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => updateFilter({ source: s })}
            className={
              source === s
                ? "rounded-full bg-slate-900 px-3 py-1 text-xs font-medium text-white"
                : "rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700 hover:bg-slate-200"
            }
          >
            {s}
          </button>
        ))}
      </div>

      <section className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-900">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">ASIN</th>
              <th className="px-4 py-2 text-left">Product</th>
              <th className="px-4 py-2 text-left">Brand</th>
              <th className="px-4 py-2 text-left">Source</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Details</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-slate-400">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && scrapes.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-slate-400">
                  No scrapes {status ? `with status "${status}"` : ""}
                  {source ? ` from source "${source}"` : ""}.
                </td>
              </tr>
            )}
            {scrapes.map((a) => (
              <tr key={a.id} className="text-slate-700">
                <td className="px-4 py-3 font-mono text-xs text-slate-700">
                  {a.asin}
                </td>
                <td className="px-4 py-3 max-w-xs truncate text-slate-700">
                  {a.productTitle || "—"}
                </td>
                <td className="px-4 py-3 text-slate-700">{a.brandName || "—"}</td>
                <td className="px-4 py-3 text-xs">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-700">
                    {a.source}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={a.status} claimed={!!a.manualScraper} />
                </td>
                <td className="max-w-sm px-4 py-3 text-xs text-slate-600">
                  {a.errorMessage ? (
                    <span title={a.errorMessage}>
                      {a.errorCategory || "Failure"} · {snippet(a.errorMessage, 96)}
                    </span>
                  ) : a.manualScraper ? (
                    <span>{a.manualScraper.name || a.manualScraper.email}</span>
                  ) : (
                    <span className="text-slate-400">
                      {a.source === "human" ? "unclaimed" : a.coverage || "—"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/admin/scrapes/${a.id}`}
                    className="text-xs text-slate-700 hover:text-slate-900 underline-offset-4 hover:underline"
                  >
                    Open →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </AdminLayout>
  );
}

export default function AdminScrapes() {
  return (
    <AdminGate>
      <AdminScrapesInner />
    </AdminGate>
  );
}

function snippet(value: string, max: number) {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function StatusBadge({
  status,
  claimed,
}: {
  status: Scrape["status"];
  claimed: boolean;
}) {
  const display = status === "pending" && claimed ? "claimed" : status;
  const map: Record<Scrape["status"] | "claimed", string> = {
    pending: "bg-slate-100 text-slate-700",
    claimed: "bg-sky-50 text-sky-700",
    submitted: "bg-amber-50 text-amber-700",
    running: "bg-sky-50 text-sky-700",
    succeeded: "bg-emerald-50 text-emerald-700",
    failed: "bg-red-50 text-red-700",
    rejected: "bg-red-50 text-red-700",
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${map[display]}`}
    >
      {display.replace("_", " ")}
    </span>
  );
}
